import { useCallback, useEffect, useRef, useState } from "react";
import { EmailListItem, MailDirection, SearchEmails } from "./EmailSearchAPI";
import { clampPage, DEFAULT_FILTERS, EmailFilters, forTenant, toCriteria } from "./searchCriteria";
import { searchErrorMessages } from "./searchErrors";
import { useTenantToken } from "./useTenantToken";

/**
 * The seven ways a search can end, plus loading.
 *
 * `notEntitled` and `forbidden` are SEPARATE and must stay separate. The first
 * means the STS refused to elevate this admin into that tenant; the second
 * means elevation worked and the tenant's own API still said no. They indict
 * different systems, and that difference is the only thing telling an operator
 * which team to contact — the same reasoning that keeps the Core view's
 * `Failing` and `Stale` from collapsing into one badge.
 *
 * `rejected` is a `400`, which this UI treats as a CLIENT BUG rather than user
 * error: `searchCriteria` constrains every control so a rejection is
 * unreachable. One that arrives anyway means a control got away from us.
 *
 * `empty` is separate from `ready` so the view can say "no emails matched"
 * rather than render an empty table, which is indistinguishable from a
 * still-loading one.
 */
export type SearchState =
    "loading" | "ready" | "empty" | "notEntitled" | "unauthorised" | "forbidden" | "rejected" | "failed";

export interface iUseEmailSearchProps {
    /** The tenant being searched. Changing it re-runs the query. */
    tenantKey: string;
    /** The app-wide selected tenant, whose token is already minted. */
    defaultTenantKey?: string;
    /** `tenantContext.selectedTenant.token`. Used only for `defaultTenantKey`. */
    defaultToken?: string;
}

export interface iUseEmailSearch {
    state: SearchState;
    items: EmailListItem[];
    /**
     * The direction the rows in `items` actually came from.
     *
     * NOT the same as `filters.direction`, which flips the moment an operator
     * switches mode while the previous direction's rows are still on screen.
     * `EmailTable` picks its columns from the direction it is given and its cells
     * from each row, so handing it `filters.direction` during that window renders
     * a header and a body that disagree about how many columns there are.
     */
    itemsDirection: MailDirection;
    totalCount: number;
    asOf?: string;
    page: number;
    filters: EmailFilters;
    /** Messages from a `400`. Empty in every other state. */
    errors: string[];
    /**
     * True while a request is in flight AND there is already something on screen.
     *
     * Distinct from `state === "loading"`, which means there is nothing to show
     * yet: the two are never true at once, so a header button reading
     * "Refreshing…" cannot appear before the page has ever loaded anything. A
     * refresh deliberately keeps the previous rows and the current state
     * visible — this is a diagnostic view, and an operator refreshing while
     * watching a queue drain should not lose the table on every click.
     * `useTenantInspection` draws the same line for the same reason.
     */
    refreshing: boolean;
    setPage: (page: number) => void;
    /** Resets to page 1 — a changed query is a different result set. */
    setFilters: (filters: EmailFilters) => void;
    refresh: () => void;
}

/** `getTenantAuth` throws bare message strings rather than typed errors. */
function stateForMintError(error: unknown): SearchState {
    if (error instanceof Error && error.message === "403") {
        return "notEntitled";
    }
    if (error instanceof Error && error.message === "401") {
        return "unauthorised";
    }
    return "failed";
}

function stateForFetchStatus(status: number): SearchState {
    if (status === 403) {
        return "forbidden";
    }
    if (status === 401) {
        return "unauthorised";
    }
    if (status === 400) {
        return "rejected";
    }
    return "failed";
}

/**
 * One page of one tenant's emails.
 *
 * Searching the app-wide selected tenant costs no token mint; every other
 * tenant goes through the shared cache. `useTenantToken` owns that rule and
 * explains it.
 */
export function useEmailSearch({ tenantKey, defaultTenantKey, defaultToken }: iUseEmailSearchProps): iUseEmailSearch {
    const [state, setState] = useState<SearchState>("loading");
    const [items, setItems] = useState<EmailListItem[]>([]);
    const [itemsDirection, setItemsDirection] = useState<MailDirection>(DEFAULT_FILTERS.direction);
    const [totalCount, setTotalCount] = useState(0);
    const [asOf, setAsOf] = useState<string | undefined>(undefined);
    const [errors, setErrors] = useState<string[]>([]);
    const [page, setPageState] = useState(1);
    const [filters, setFiltersState] = useState<EmailFilters>(DEFAULT_FILTERS);
    const [refreshing, setRefreshing] = useState(false);
    const [reloadToken, setReloadToken] = useState(0);

    // Bumped on entry and by the effect cleanup, and checked after every await.
    // A tenant switch or a filter change mid-flight must not let the older
    // response land. Same shape as useTenantInspection's counter.
    const requestId = useRef(0);

    // Identity-stable by construction; see useTenantToken. Shared with the
    // email detail, so a drill-down does not mint a second time.
    const tokenFor = useTenantToken({ defaultTenantKey, defaultToken });

    /** False until the first request settles, so a first load is `loading`, not `refreshing`. */
    const hasSettled = useRef(false);

    // Adjusting state during render, React's documented pattern for "a prop
    // changed and some state derived from it is now wrong". Doing this in an
    // effect instead would let one search fire against the new tenant carrying
    // the old tenant's mailboxID, invalidated only by the ordering of a
    // re-render against an awaited promise — correct today, but by accident.
    const [lastTenantKey, setLastTenantKey] = useState(tenantKey);
    if (tenantKey !== lastTenantKey) {
        setLastTenantKey(tenantKey);
        // Mailbox ids are per-tenant, so a carried-over id silently filters every
        // row out. Dates, text and direction mean the same thing in any tenant.
        setFiltersState(current => forTenant(current));
        setPageState(1);

        // A DIFFERENT TENANT IS A DIFFERENT QUESTION, so this is a fresh load
        // and not a refresh.
        //
        // `refreshing` deliberately keeps the previous rows on screen — an
        // operator watching a queue drain should not lose the table on every
        // click. That reasoning is about re-asking the SAME question. Carrying
        // rows across a tenant switch instead puts one tenant's worklist under
        // another tenant's key for a whole round trip, with nothing marking it
        // stale, which is the same defect the consumer's mailbox-name map is
        // cleared for — on much stronger evidence than a mailbox name.
        //
        // Done here rather than in the view so no consumer can get it wrong.
        setItems([]);
        setTotalCount(0);
        setAsOf(undefined);
        setState("loading");

        // Reset WITH the state above, never separately. `refreshing` is set
        // from this ref on effect entry, so a "loading" state over a settled
        // ref would report loading and refreshing at once — and their being
        // mutually exclusive is what stops a header reading "Refreshing…" over
        // a page with nothing on it. `itemsDirection` needs no reset: nothing
        // reads it while `items` is empty, and the next response sets it.
        hasSettled.current = false;
    }

    useEffect(() => {
        requestId.current += 1;
        const thisRequest = requestId.current;
        const isCurrent = () => requestId.current === thisRequest;

        // `refreshing` means "re-reading over something already on screen", so
        // it stays false until a request has actually settled. Before that
        // `state` is still "loading" and the two would otherwise both be true,
        // which reads as "Refreshing…" on a page that has never loaded.
        setRefreshing(hasSettled.current);
        setErrors([]);

        void (async () => {
            try {
                let token: string;
                try {
                    token = await tokenFor(tenantKey);
                    if (!isCurrent()) {
                        return;
                    }
                } catch (error) {
                    if (isCurrent()) {
                        setState(stateForMintError(error));
                    }
                    return;
                }

                // Captured before the `await`, from the criteria that produced
                // this response — not read from `filters` after the response
                // lands, which would be the same stale-read bug one level up:
                // `filters.direction` can have flipped again by then too.
                const criteria = toCriteria(filters, page);
                const requestedDirection = criteria.direction;

                const response = await SearchEmails(token, criteria);
                if (!isCurrent()) {
                    return;
                }

                if (!response.isOk) {
                    setState(stateForFetchStatus(response.status));
                    if (response.status === 400) {
                        setErrors(searchErrorMessages(response.validationErrors));
                    }
                    return;
                }

                // `?? []` on a COLLECTION, which the conventions permit with a
                // reason: a 200 carrying no items and one carrying [] are the
                // same answer — nothing matched. There is no third state.
                const rows = response.data?.items ?? [];
                const total = response.data?.totalCount ?? 0;

                // Paging past the end returns 200 with an empty page and the
                // true total, so a narrowed filter must pull the page back into
                // range rather than leave an unexplained empty table.
                //
                // Checked BEFORE the rows are committed, deliberately. That page
                // is empty by construction, and committing it would sit the hook
                // at "ready" with no rows for a whole round trip — the empty
                // table the separate `empty` state exists to prevent — and would
                // leave the out-of-range page behind a failure if the re-run
                // then broke, instead of the previous rows.
                const inRange = clampPage(page, total, filters.itemsPerPage);
                if (inRange !== page) {
                    setPageState(inRange);
                    return;
                }

                setItems(rows);
                setItemsDirection(requestedDirection);
                setTotalCount(total);
                setAsOf(response.data?.asOf);
                setState(rows.length === 0 ? "empty" : "ready");
            } finally {
                // try/finally rather than a check at each return: there are
                // several exit paths, and a flag left stuck true after any one
                // of them permanently disables the refresh control.
                if (isCurrent()) {
                    hasSettled.current = true;
                    setRefreshing(false);
                }
            }
        })();

        return () => {
            requestId.current += 1;
        };
    }, [tenantKey, filters, page, reloadToken, tokenFor]);

    const setFilters = useCallback((next: EmailFilters) => {
        setFiltersState(next);
        // A changed query is a different result set; page 4 of the old one
        // means nothing in the new one.
        setPageState(1);
    }, []);

    const refresh = useCallback(() => setReloadToken(token => token + 1), []);

    return {
        state,
        items,
        itemsDirection,
        totalCount,
        asOf,
        page,
        filters,
        errors,
        refreshing,
        setPage: setPageState,
        setFilters,
        refresh,
    };
}
